import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Text,
  TokenTextAppearance,
  styled,
  Spinner,
  CTA,
  css,
  TextTag,
  TextAlignment,
} from '@volkswagen-onehub/components-core';
import { Slider } from '@org-avp/avp-hck-dga-ui-lib';
import { ChevronLeft, ChevronRight } from '@volkswagen-onehub/icons-core';
import { useTheme } from 'styled-components';
import { minimalScreenWidth } from '@/common/breakpoints';
import { Navigation } from '@/enums/Navigation';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import { TrackingEvent } from '@/util/tracking/definitions';
import { ClusteredButtonContainer } from '../clusterButton/ClusterButtonContainer';
import { GALLERY_DATA } from '../clusterButton/ClusterButton.types';
import { Lentils } from '../lentils/Lentils';
import { CLUSTER_BUTTON_TYPES } from '@/enums/ClusterButton';

export type GalleryProps = {
  assets?: string[];
  labelPrefix: string;
  name: CLUSTER_BUTTON_TYPES;
  displaySubTitle?: boolean;
  tabData?: GALLERY_DATA[];
  displayTabs?: boolean;
  onImageClick?: (src: string, index: number) => void;
  sliderPadding?: string;
};

const StyledGalleryContainer = styled.div`
  margin-bottom: ${(props) => props.theme.size.dynamic0100};
`;

const StyledGallery = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const StyledSubTitleWrapper = styled(Text)`
  padding-top: ${(props) => props.theme.size.dynamic0350};
  padding-bottom: ${(props) => props.theme.size.dynamic0130};
  @media screen and ${minimalScreenWidth.m} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }

  @media screen and ${minimalScreenWidth.l} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }

  @media screen and ${minimalScreenWidth.xxl} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }
`;

const StyledCTA = styled(CTA)`
  width: 12px;
  height: 12px;
`;

const StyledClusterControls = styled.div`
  display: flex;
  justify-content: end;
  z-index: 1;
  align-items: end;
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
  pointer-events: none;
  margin: ${({ theme }) => `${theme.size.static530} ${theme.size.static250}`};
`;

export const customPadding = (theme) => css`
  padding: 0 ${theme.size.dynamic0120} 0 ${theme.size.dynamic0120};

  @media screen and ${minimalScreenWidth.s} {
    padding: 0 ${theme.size.dynamic0120} 0 ${theme.size.dynamic0120};
  }
`;

export const ImageGallery = (props: GalleryProps): ReactElement<GalleryProps> | null => {
  const {
    labelPrefix,
    name,
    displaySubTitle,
    tabData,
    assets,
    displayTabs,
    onImageClick,
    sliderPadding,
  } = props;
  const [activeTab, setActiveTab] = useState<CLUSTER_BUTTON_TYPES>(
    tabData?.[0]?.name || CLUSTER_BUTTON_TYPES.UNKNOWN,
  );
  const [imageAssets, setImageAssets] = useState<string[]>(tabData?.[0]?.images || assets || []);

  if (!imageAssets?.length) {
    return null;
  }

  const theme = useTheme();
  const { t } = useTranslation();
  const trackingService = useTrackingService();

  const findAssetsByTabName = (tabName: CLUSTER_BUTTON_TYPES) =>
    tabData?.find((data) => data.name === tabName);

  const handleTabChange = (tabName: CLUSTER_BUTTON_TYPES) => {
    setActiveTab(tabName);
    const galleryAssets = findAssetsByTabName(tabName);

    if (galleryAssets) {
      setImageAssets(galleryAssets.images);
    }
  };

  const trackEvent = (type: string, index: number) => {
    trackingService.track(TrackingEvent.VWBasicSliderElementLoad, {
      content: { Name: type, ItemCount: imageAssets.length, ItemPosition: index + 1 },
      eventInfo: { contentId: `trigger slider ${type}_${index}` },
    });
  };

  const customPagination = useCallback(
    ({ selectedIndex, slidePosition, onPaginationClick }) => (
      <Lentils
        activeIndex={selectedIndex}
        slideLength={slidePosition.length}
        slideKeys={slidePosition.map((index) => `slide-${index}`)}
        onChange={onPaginationClick}
      />
    ),
    [],
  );

  const customPrevButton = useCallback(
    ({ onClick, disabled }) => (
      <StyledCTA
        size="small"
        tag="button"
        emphasis="secondary"
        disabled={disabled}
        title="previous button"
        icon={<ChevronLeft aria-hidden="true" />}
        data-testid="galleryControl-previous"
        onClick={onClick}
      />
    ),
    [],
  );

  const customNextButton = useCallback(
    ({ onClick, disabled }) => (
      <StyledCTA
        size="small"
        tag="button"
        emphasis="secondary"
        title="next button"
        disabled={disabled}
        icon={<ChevronRight aria-hidden="true" />}
        data-testid="galleryControl-next"
        onClick={onClick}
      />
    ),
    [],
  );

  return (
    <StyledGalleryContainer id={Navigation.GALLERY} data-testid={`${name}-gallery-container`}>
      {displaySubTitle && (
        <StyledSubTitleWrapper
          tag={TextTag.h2}
          textAlign={TextAlignment.center}
          appearance={TokenTextAppearance.headline400}
          bold
          id={`${name}-gallery-description`}
        >
          {t(`body.details.gallery.${name}`)}
        </StyledSubTitleWrapper>
      )}
      {tabData && displayTabs && (
        <StyledClusterControls data-testid="detail-gallery-clusterButtons">
          <ClusteredButtonContainer
            tabs={tabData}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </StyledClusterControls>
      )}
      <StyledGallery data-testid={`${name}-gallery`}>
        <Slider
          slides={imageAssets}
          sliderPadding={sliderPadding ? sliderPadding : customPadding(theme)}
          customPagination={customPagination}
          customPrevButton={customPrevButton}
          ariaLabel={`${labelPrefix}`}
          customNextButton={customNextButton}
          customSpinner={<Spinner variant="large" />}
          objectFit="contain"
          {...(onImageClick && {
            onImageClick: ({ src, index }) => onImageClick(src, index),
          })}
          onSlideChange={(currentIndex) => {
            trackEvent(name, currentIndex);
          }}
        />
      </StyledGallery>
    </StyledGalleryContainer>
  );
};
